"use client";

import { Check, Coins, Wallet } from "lucide-react";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { inr } from "@/features/activity-session/components/split/split-copy";
import {
  blockedShareMinor,
  extrasAllowanceMinor,
  minimumMeaning,
  organizerCollectsMinor,
  previewPerHeadMinor,
  type SplitPricingShape,
  worstCaseShareMinor,
} from "@/lib/activity-sessions/split";
import type { SimpleFormApi } from "@/lib/form";
import { cn } from "@/lib/utils";
import type { ActivitySessionValues } from "@/validations/activity-session";
import { coerceDecimalToMinor } from "./activity-session-drawer.utils";

export function SessionSplitCreationFields({
  form,
}: {
  form: SimpleFormApi<ActivitySessionValues>;
}) {
  return (
    <div className="space-y-4">
      <form.Field name="collectMoney">
        {(field) => (
          <div className="flex items-start justify-between gap-4 rounded-xl border border-border px-3 py-2.5">
            <div className="space-y-1">
              <label
                htmlFor="collect-money"
                className="text-sm font-medium text-text-primary"
              >
                Collect money for this session
              </label>
              <p className="text-xs text-text-secondary">
                Set the terms now so every player sees and agrees to them before
                joining.
              </p>
            </div>
            <Switch
              id="collect-money"
              checked={field.state.value}
              onCheckedChange={(checked) => {
                field.handleChange(Boolean(checked));
                if (!checked) {
                  form.setFieldValue("pricingType", "free");
                  form.setFieldValue("pricePerPersonAmountMinor", 0);
                }
              }}
            />
          </div>
        )}
      </form.Field>

      <form.Subscribe
        selector={(state) => ({
          collectMoney: state.values.collectMoney,
          pricing: state.values.splitPricing,
          mode: state.values.splitMode,
          amountMinor: state.values.splitAmountMinor,
          organizerPlaying: state.values.organizerPlaying,
          extrasAllowed: state.values.extrasAllowed,
          extrasPercent: state.values.extrasAllowancePercent,
          minParticipants: state.values.minParticipants,
          maxParticipants: state.values.maxParticipants,
        })}
      >
        {(values) => {
          if (!values.collectMoney) {
            return (
              <p className="px-1 text-xs text-text-tertiary">
                Leave this off for a free session. You can still add a split
                later, before anyone joins.
              </p>
            );
          }

          const shape: SplitPricingShape =
            values.pricing === "divide_total"
              ? {
                  pricing: "divide_total",
                  totalAmountMinor: values.amountMinor,
                }
              : {
                  pricing: "fixed_per_person",
                  perPersonAmountMinor: values.amountMinor,
                };
          const hasAmount = values.amountMinor > 0;
          const minimum = hasAmount
            ? minimumMeaning({
                shape,
                minParticipants: values.minParticipants,
                organizerPlaying: values.organizerPlaying,
                formatMinor: inr,
              })
            : null;
          const worstCase = hasAmount
            ? worstCaseShareMinor({
                shape,
                minParticipants: values.minParticipants,
                organizerPlaying: values.organizerPlaying,
              })
            : 0;
          const allowance = extrasAllowanceMinor(worstCase, {
            extrasAllowed: values.extrasAllowed,
            extrasAllowancePercent: values.extrasPercent,
          });
          const blocked = hasAmount
            ? blockedShareMinor({
                shape,
                minParticipants: values.minParticipants,
                organizerPlaying: values.organizerPlaying,
                policy: {
                  extrasAllowed: values.extrasAllowed,
                  extrasAllowancePercent: values.extrasPercent,
                },
              })
            : 0;
          const ladder: Array<{ count: number; label: string }> = [];
          if (hasAmount) {
            for (
              let count = Math.max(1, values.minParticipants);
              count <=
              Math.min(values.maxParticipants, values.minParticipants + 3);
              count += 1
            ) {
              ladder.push({
                count,
                label:
                  values.pricing === "divide_total"
                    ? `${inr(previewPerHeadMinor(shape, count))} each`
                    : `you collect ${inr(
                        organizerCollectsMinor(
                          shape,
                          count,
                          values.organizerPlaying,
                        ),
                      )}`,
              });
            }
          }

          return (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <PricingCard
                  selected={values.pricing === "divide_total"}
                  onSelect={() => {
                    form.setFieldValue("splitPricing", "divide_total");
                    form.setFieldValue("organizerPlaying", true);
                  }}
                  title="Split a cost"
                  detail="₹1,200 for the pitch, shared by whoever plays. Per-head falls as people join."
                />
                <PricingCard
                  selected={values.pricing === "fixed_per_person"}
                  onSelect={() => {
                    form.setFieldValue("splitPricing", "fixed_per_person");
                    form.setFieldValue("organizerPlaying", false);
                  }}
                  title="Fixed price"
                  detail="₹300 each, whoever turns up. Nothing to divide, nothing moves."
                />
              </div>

              <form.Field name="splitAmountMinor">
                {(field) => (
                  <div className="space-y-2">
                    <label
                      htmlFor="create-split-amount"
                      className="text-sm font-medium text-text-secondary"
                    >
                      {values.pricing === "divide_total"
                        ? "Total to split"
                        : "Price per person"}
                    </label>
                    <div className="relative">
                      <span className="absolute top-1/2 left-3 -translate-y-1/2 text-text-tertiary">
                        ₹
                      </span>
                      <MinorCurrencyInput
                        id="create-split-amount"
                        valueMinor={field.state.value}
                        onChangeMinor={field.handleChange}
                        placeholder={
                          values.pricing === "divide_total" ? "1200" : "300"
                        }
                      />
                    </div>
                  </div>
                )}
              </form.Field>

              {minimum ? (
                <div className="rounded-xl border border-border bg-surface-alt/50 p-3">
                  <p className="text-sm font-medium text-text-primary">
                    {minimum.headline}
                  </p>
                  <p className="mt-1 text-xs text-text-tertiary">
                    {minimum.detail}
                  </p>
                  {values.extrasAllowed && values.mode === "hiive_money" ? (
                    <p className="mt-2 border-t border-border/60 pt-2 text-xs text-text-secondary">
                      <span className="font-medium text-text-primary">
                        {inr(blocked)} blocked
                      </span>{" "}
                      ({inr(worstCase)} + up to {inr(allowance)} extras)
                    </p>
                  ) : null}
                </div>
              ) : null}

              {ladder.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {ladder.map((row) => (
                    <span
                      key={row.count}
                      className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-text-secondary"
                    >
                      <span className="font-medium text-text-primary">
                        {row.count}
                      </span>{" "}
                      {row.label}
                    </span>
                  ))}
                </div>
              ) : null}

              <form.Field name="organizerPlaying">
                {(field) => (
                  <div className="flex items-center justify-between gap-4 rounded-xl border border-border p-3">
                    <label htmlFor="create-organizer-playing">
                      <span className="block text-sm font-medium text-text-primary">
                        I'm playing too
                      </span>
                      <span className="block text-xs text-text-tertiary">
                        {values.pricing === "divide_total"
                          ? "Counts you in the divisor. Your share is shown as paid directly because you covered the venue."
                          : "Coaches and hosts usually aren't one of the people paying."}
                      </span>
                    </label>
                    <Switch
                      id="create-organizer-playing"
                      checked={field.state.value}
                      onCheckedChange={(checked) =>
                        field.handleChange(Boolean(checked))
                      }
                    />
                  </div>
                )}
              </form.Field>

              <div className="rounded-xl border border-border p-3">
                <form.Field name="extrasAllowed">
                  {(field) => (
                    <div className="flex items-center justify-between gap-4">
                      <label htmlFor="create-extras-allowed">
                        <span className="block text-sm font-medium text-text-primary">
                          Extras may be added after the game
                        </span>
                        <span className="block text-xs text-text-tertiary">
                          Drinks, an extra hour, shuttles. Everyone sees the
                          ceiling before they join.
                        </span>
                      </label>
                      <Switch
                        id="create-extras-allowed"
                        checked={field.state.value}
                        onCheckedChange={(checked) =>
                          field.handleChange(Boolean(checked))
                        }
                      />
                    </div>
                  )}
                </form.Field>

                {values.extrasAllowed ? (
                  <div className="mt-3 space-y-3 border-t border-border/60 pt-3">
                    <form.Field name="extrasAllowancePercent">
                      {(field) => (
                        <div>
                          <div className="flex items-baseline justify-between gap-3">
                            <label
                              htmlFor="create-extras-percent"
                              className="text-sm font-medium text-text-secondary"
                            >
                              Up to
                            </label>
                            <span className="text-right text-sm font-semibold text-text-primary tabular-nums">
                              {field.state.value}% of the base
                              {hasAmount ? ` · ${inr(allowance)} each` : ""}
                            </span>
                          </div>
                          <input
                            id="create-extras-percent"
                            type="range"
                            min={0}
                            max={100}
                            step={5}
                            value={field.state.value}
                            onChange={(event) =>
                              field.handleChange(Number(event.target.value))
                            }
                            className="mt-2 w-full accent-brand"
                          />
                          <p className="mt-1 text-xs text-text-tertiary">
                            You won't be able to add more than this per person.
                          </p>
                        </div>
                      )}
                    </form.Field>

                    <form.Field name="extrasNote">
                      {(field) => (
                        <Input
                          id="create-extras-note"
                          value={field.state.value}
                          onChange={(event) =>
                            field.handleChange(event.target.value)
                          }
                          placeholder="drinks, extra hour, shuttles"
                          maxLength={200}
                        />
                      )}
                    </form.Field>
                  </div>
                ) : null}
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-text-secondary">
                  How do you want to collect it?
                </p>
                <ModeCard
                  selected={values.mode === "offline"}
                  onSelect={() => form.setFieldValue("splitMode", "offline")}
                  icon={<Coins className="size-4" />}
                  title="Cash or UPI, tracked here"
                  detail="Hiive keeps the list of who owes what. You mark rows settled as the money lands."
                />
                <ModeCard
                  selected={values.mode === "hiive_money"}
                  onSelect={() =>
                    form.setFieldValue("splitMode", "hiive_money")
                  }
                  icon={<Wallet className="size-4" />}
                  title="Hiive Money — guaranteed"
                  detail={
                    values.extrasAllowed
                      ? `We block ${
                          hasAmount ? inr(blocked) : "the ceiling"
                        } — the most anyone can owe, including extras.`
                      : values.pricing === "fixed_per_person"
                        ? "Each player's price is blocked when they join and paid to you after the session."
                        : "Each player's worst case is blocked when they join; unused money goes straight back."
                  }
                />
              </div>
            </div>
          );
        }}
      </form.Subscribe>
    </div>
  );
}

function MinorCurrencyInput({
  id,
  valueMinor,
  onChangeMinor,
  placeholder,
}: {
  id: string;
  valueMinor: number;
  onChangeMinor: (minor: number) => void;
  placeholder: string;
}) {
  const [draft, setDraft] = useState(
    valueMinor > 0 ? String(valueMinor / 100) : "",
  );

  return (
    <Input
      id={id}
      inputMode="decimal"
      value={draft}
      onChange={(event) => {
        setDraft(event.target.value);
        onChangeMinor(coerceDecimalToMinor(event.target.value));
      }}
      onBlur={() =>
        setDraft(valueMinor > 0 ? (valueMinor / 100).toFixed(2) : "")
      }
      placeholder={placeholder}
      className="pl-7"
    />
  );
}

function PricingCard({
  selected,
  onSelect,
  title,
  detail,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  detail: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "rounded-xl border p-3 text-left transition-colors",
        selected
          ? "border-brand bg-brand/5"
          : "border-border bg-surface hover:bg-surface-alt/60",
      )}
    >
      <span className="flex items-center gap-1.5">
        <span className="text-sm font-semibold text-text-primary">{title}</span>
        {selected ? <Check className="size-3.5 text-brand" /> : null}
      </span>
      <span className="mt-1 block text-xs text-text-tertiary">{detail}</span>
    </button>
  );
}

function ModeCard({
  selected,
  onSelect,
  icon,
  title,
  detail,
}: {
  selected: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  title: string;
  detail: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full gap-3 rounded-xl border p-3 text-left transition-colors",
        selected
          ? "border-brand bg-brand/5"
          : "border-border bg-surface hover:bg-surface-alt/60",
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg",
          selected
            ? "bg-brand/15 text-brand"
            : "bg-surface-alt text-text-secondary",
        )}
      >
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-text-primary">
          {title}
        </span>
        <span className="mt-0.5 block text-xs text-text-tertiary">
          {detail}
        </span>
      </span>
    </button>
  );
}
